import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { StorageService } from './storage.service'

export type AppTheme = 'light' | 'dark'

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentTheme: AppTheme = 'light'

  private themeSubject = new BehaviorSubject<AppTheme>('light')

  //Emits the active theme; chart components subscribe to re-theme on toggle
  public theme$: Observable<AppTheme> = this.themeSubject.asObservable()

  constructor (private storageService: StorageService) {}

  //Sync the service with the saved preference on startup. The index.html boot
  //script has already applied the correct stylesheet to avoid a flash; this
  //keeps the in-memory state and body attribute consistent.
  public init (): void {
    this.setTheme(this.storageService.getTheme())
  }

  public get theme (): AppTheme {
    return this.currentTheme
  }

  public get isDark (): boolean {
    return this.currentTheme === 'dark'
  }

  //Text colour for ApexCharts axes/legend/labels/title. Dark mode uses a bright
  //near-white; light mode uses the app's body grey (--bs-body-color: #6c757d)
  //instead of ApexCharts' near-black default (#373d3f), which read as harsh.
  public get apexForeColor (): string {
    return this.currentTheme === 'dark' ? '#e9ecef' : '#6c757d'
  }

  //Grid/border colour for ApexCharts in dark mode (undefined = default light)
  public get apexGridColor (): string | undefined {
    return this.currentTheme === 'dark' ? '#3e4555' : undefined
  }

  public toggle (): void {
    this.setTheme(this.currentTheme === 'dark' ? 'light' : 'dark')
  }

  public setTheme (theme: AppTheme): void {
    this.currentTheme = theme === 'dark' ? 'dark' : 'light'

    //Swap the active AppStack stylesheet
    const link = document.getElementById('appTheme')
    if (link) {
      link.setAttribute('href', `theme-css/${this.currentTheme}.css`)
    }

    //Keep AppStack's data-theme attribute in sync (default = light)
    const attribute = this.currentTheme === 'dark' ? 'dark' : 'default'
    document.documentElement.setAttribute('data-theme', attribute)
    document.body.setAttribute('data-theme', attribute)

    this.storageService.setTheme(this.currentTheme)
    this.applyApexDefaults()
    this.themeSubject.next(this.currentTheme)
  }

  //Global ApexCharts defaults so charts rendered later (e.g. after navigation)
  //pick up the theme even before their component subscription fires.
  //Deliberately does NOT set ApexCharts `theme.mode` — that paints a dark plot
  //background onto the chart instance which is not reset when toggling back to
  //light. We only nudge the font, text and grid colour. `fontFamily: 'inherit'`
  //makes every chart (axes, legend, title) use the app font (Titillium) so the
  //dashboard and charge-history charts look consistent with each other and the
  //rest of the UI, in both themes.
  private applyApexDefaults (): void {
    const apexWindow = window as unknown as { Apex?: Record<string, unknown> }
    apexWindow.Apex = {
      chart: {
        fontFamily: 'inherit',
        foreColor: this.apexForeColor
      },
      tooltip: { theme: this.currentTheme },
      grid: this.apexGridColor ? { borderColor: this.apexGridColor } : {}
    }
  }
}
